import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, Paper } from '@mui/material';
import * as echarts from 'echarts';
import axios from 'axios';
import { USER } from "../../../api/vsc";
import './index.css';

// --- ASSET IMPORTS ---
import BlogIcon from './assets/blog.png';
import VideoIcon from './assets/video.png';
import EventIcon from './assets/event.png';
import MentorIcon from './assets/mentor.png';
import VolunteerIcon from './assets/volunteer.png';
import DiscussionIcon from './assets/discussion.png';

const scoreCards = [
  { title: "Blogs", time: "10 min", img: BlogIcon, active: false },
  { title: "Videos", time: "5 min", img: VideoIcon, active: false },
  { title: "Events", time: "30 min", img: EventIcon, active: false },
  { title: "Mentorship", time: "30 min", img: MentorIcon, active: false },
  { title: "Volunteer", time: "30 min", img: VolunteerIcon, active: false },
  { title: "Discussion", time: "20 min", img: DiscussionIcon, active: false }
];

const quotesList = [
  "The first step is always small, but it opens the door to every future victory.",
  "Dreams remain dreams until your feet decide to move.",
  "Every extraordinary journey once looked like an ordinary beginning.",
  "The hardest distance to cross is the one between thinking and doing.",
  "Start before you feel ready; readiness grows along the path.",
  "The future rewards the person who takes action today.",
  "A single step in courage is louder than a thousand plans in silence.",
  "The road appears only after you start walking.",
  "Progress begins where excuses end.",
  "Fear shrinks the moment you move toward it.",
  "The first brave act is believing you deserve success."
];

const Dashboard = () => {
  const [chartData, setChartData] = useState([0, 0, 0, 0, 0]);
  const [quote, setQuote] = useState("");

  // Refs for raw ECharts integration
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const userName = localStorage.getItem("name") || "Karthik";
  const categories = ['Watchnows', 'Videos', 'Blogs', 'CaseStudies', 'Badges'];

  // --- 1. Fetch Data ---
  useEffect(() => {
    // Random Quote Selection
    const randomIndex = Math.floor(Math.random() * quotesList.length);
    setQuote(quotesList[randomIndex]);

    // Fetch User Progress
    axios.get(USER, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        // Safe fallback to empty array to prevent fatal reduce crashes
        const courseDetails = response.data?.courseDetails || [];

        const totalVideos = courseDetails.reduce((total, course) => {
          const completedCount = course?.videosCompleted?.length || 0;
          return total + completedCount;
        }, 0);

        setChartData([
          response.data?.completedWatchnow?.length || 0,
          totalVideos,
          response.data?.completedBlog?.length || 0,
          response.data?.completedCaseStudy?.length || 0,
          response.data?.completedBadges?.length || 0,
        ]);
      })
      .catch((error) => console.error("Dashboard Data Error:", error));
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
      
      const resizeHandler = () => chartInstance.current?.resize();
      window.addEventListener("resize", resizeHandler);
    }

    const options = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: '{b}: <b>{c} Items</b>',
        textStyle: { fontFamily: 'Inter, sans-serif' }
      },
      grid: {
        top: '5%',
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#888', fontSize: 12, fontFamily: 'Inter, sans-serif' }
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
        axisLabel: { color: '#888', fontFamily: 'Inter, sans-serif' }
      },
      series: [
        {
          name: 'Completed Count',
          type: 'bar',
          barWidth: '45%',
          data: chartData.map(val => ({
            value: val,
            itemStyle: {
              color: val > 0 ? '#6c5ce7' : '#f5f5f5',
              borderRadius: [4, 4, 0, 0]
            }
          }))
        }
      ]
    };
    chartInstance.current.setOption(options);

  }, [chartData]); 

  // --- 3. Render ---
  return (
    // Single Root Element Wrap
    <Box className="db-container">

      {/* HEADER SECTION */}
      <Box mb={4}>
        <Typography variant="h3" fontWeight="800" className="db-greeting">
          Hola {userName},
        </Typography>
        <Typography variant="body1" className="db-quote" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
          "{quote}"
        </Typography>
      </Box>

      {/* STATUS CARDS */}
      <Grid container spacing={3} mb={4}>
        {[
          { label: "Applied for", value: "Volunteer" },
          { label: "Our Platform Active Page", value: "Blog" },
          { label: "Your", value: "Profile" }
        ].map((card, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper elevation={0} className="db-status-card">
              <Typography variant="caption" color="textSecondary">{card.label}</Typography>
              <Typography variant="h5" fontWeight="700">{card.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* RAW ECHARTS SECTION */}
      <Paper elevation={0} className="db-chart-wrapper">
        <Typography variant="subtitle2" fontWeight="700" mb={2}>
          Your Activity Breakdown (Live Data)
        </Typography>
        <Box
          ref={chartRef}
          style={{ height: '270px', width: '100%' }}
        />
      </Paper>

      {/* SCORE IMPROVEMENT GRID */}
      <Box mt={5}>
        <Typography variant="h6" fontWeight="700" mb={3}>Improve Your Membership Score</Typography>
        <Grid container spacing={2}>
          {scoreCards.map((item, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Box className={`db-score-card ${item.active ? 'active' : ''}`}>
                <div className="score-icon-box">
                  <img src={item.img} alt={item.title} className="db-custom-icon" />
                </div>
                <Typography variant="subtitle2" fontWeight="800" mt={1}>{item.title}</Typography>
                <Typography variant="caption" className="score-desc">
                  Contributes {item.time} <br /> to membership
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

    </Box>
  );
};

export default Dashboard

